package com.trevorwiebe.timesheet.admin.locations.presentation

import androidx.compose.foundation.ExperimentalFoundationApi
import androidx.compose.foundation.background
import androidx.compose.foundation.clickable
import androidx.compose.foundation.combinedClickable
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.ExperimentalLayoutApi
import androidx.compose.foundation.layout.FlowRow
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.lazy.items
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.filled.Close
import androidx.compose.material3.ButtonDefaults
import androidx.compose.material3.Card
import androidx.compose.material3.Checkbox
import androidx.compose.material3.CheckboxDefaults
import androidx.compose.material3.CircularProgressIndicator
import androidx.compose.material3.FilterChip
import androidx.compose.material3.FilterChipDefaults
import androidx.compose.material3.HorizontalDivider
import androidx.compose.material3.Icon
import androidx.compose.material3.IconButton
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.RadioButton
import androidx.compose.material3.RadioButtonDefaults
import androidx.compose.material3.Scaffold
import androidx.compose.material3.Text
import androidx.compose.material3.TextButton
import androidx.compose.runtime.Composable
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.style.TextOverflow
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import androidx.compose.ui.window.Dialog
import com.trevorwiebe.timesheet.core.presentation.TopBar
import com.trevorwiebe.timesheet.core.presentation.common.BackIcon
import com.trevorwiebe.timesheet.core.presentation.common.CollapsingHeaderLayout
import com.trevorwiebe.timesheet.core.presentation.common.DialogButtonRow
import com.trevorwiebe.timesheet.core.presentation.common.ResponsiveContent
import com.trevorwiebe.timesheet.core.presentation.common.TimesheetTextField
import com.trevorwiebe.timesheet.core.presentation.common.responsiveDialogWidth
import com.trevorwiebe.timesheet.mileage.domain.model.Location
import org.koin.compose.viewmodel.koinViewModel

@Composable
fun LocationsScreen(
    viewModel: LocationsViewModel = koinViewModel(),
    onBack: () -> Unit
) {
    val state by viewModel.state.collectAsState()

    Scaffold(
        topBar = {
            if (state.selectionMode) {
                TopBar(
                    title = "${state.selectedIds.size} selected",
                    backIcon = {
                        Icon(
                            imageVector = Icons.Default.Close,
                            contentDescription = "Exit selection",
                            tint = MaterialTheme.colorScheme.tertiary
                        )
                    },
                    onBack = { viewModel.onEvent(LocationsEvent.OnExitSelectionMode) },
                    actions = {
                        TextButton(
                            onClick = { viewModel.onEvent(LocationsEvent.OnMergeClick) },
                            enabled = state.selectedIds.size >= 2,
                            colors = ButtonDefaults.textButtonColors(
                                contentColor = MaterialTheme.colorScheme.tertiary
                            )
                        ) {
                            Text("Merge", fontWeight = FontWeight.SemiBold)
                        }
                    }
                )
            } else {
                TopBar(
                    title = "Locations",
                    backIcon = { BackIcon() },
                    onBack = onBack,
                    actions = {
                        LocationsTopBarAddAction(
                            onClick = { viewModel.onEvent(LocationsEvent.OnAddLocationClick) }
                        )
                    }
                )
            }
        },
        floatingActionButton = {
            if (!state.selectionMode) {
                LocationsFab(onClick = { viewModel.onEvent(LocationsEvent.OnAddLocationClick) })
            }
        }
    ) { paddingValues ->
        ResponsiveContent(modifier = Modifier.padding(paddingValues)) {
        CollapsingHeaderLayout(
            header = {
                Column {
                    LocationsSearchField(
                        query = state.searchQuery,
                        onQueryChanged = { viewModel.onEvent(LocationsEvent.OnSearchQueryChanged(it)) }
                    )
                    LocationsFilterChipRow(
                        activeFilters = state.activeFilters,
                        onFilterToggled = { viewModel.onEvent(LocationsEvent.OnFilterToggled(it)) }
                    )
                    HorizontalDivider()
                }
            }
        ) { contentPadding ->
            if (state.isLoading) {
                Box(modifier = Modifier.fillMaxSize(), contentAlignment = Alignment.Center) {
                    CircularProgressIndicator(color = MaterialTheme.colorScheme.tertiary)
                }
            } else {
                LazyColumn(
                    modifier = Modifier.fillMaxSize(),
                    contentPadding = contentPadding
                ) {
                    items(state.visibleLocations) { location ->
                        val selected = location.id in state.selectedIds
                        // In selection mode, only locations of the locked type can be
                        // added (same-type merges only); already-selected rows stay enabled.
                        val selectable = state.selectedType == null ||
                            location.type == state.selectedType ||
                            selected
                        LocationRow(
                            location = location,
                            selectionMode = state.selectionMode,
                            selected = selected,
                            selectable = selectable,
                            onClick = {
                                if (state.selectionMode) {
                                    viewModel.onEvent(LocationsEvent.OnToggleSelection(location))
                                } else {
                                    viewModel.onEvent(LocationsEvent.OnEditLocationClick(location))
                                }
                            },
                            onLongClick = {
                                if (!state.selectionMode) {
                                    viewModel.onEvent(LocationsEvent.OnEnterSelectionMode(location))
                                }
                            }
                        )
                    }
                }
            }
        }

        if (state.showAddEditDialog) {
            AddEditLocationDialog(
                form = state.form,
                isEditing = state.editingLocation != null,
                isDeleted = state.editingLocation?.deleted == true,
                isGeocoding = state.isGeocoding,
                geocodeError = state.geocodeError,
                onEvent = viewModel::onEvent
            )
        }

        LocationMapPickerHost(
            visible = state.showMapPicker,
            latitude = state.form.latitude,
            longitude = state.form.longitude,
            onConfirm = { lat, lng ->
                viewModel.onEvent(LocationsEvent.OnPinConfirmed(lat, lng))
            },
            onDismiss = { viewModel.onEvent(LocationsEvent.OnCloseMapPicker) }
        )

        if (state.showMergeDialog) {
            MergeLocationsDialog(
                locations = state.selectedLocations,
                isMerging = state.isMerging,
                onDismiss = { viewModel.onEvent(LocationsEvent.OnDismissMergeDialog) },
                onConfirm = { keeperId -> viewModel.onEvent(LocationsEvent.OnConfirmMerge(keeperId)) }
            )
        }
        }
    }
}

@Composable
private fun LocationsSearchField(
    query: String,
    onQueryChanged: (String) -> Unit
) {
    TimesheetTextField(
        modifier = Modifier
            .fillMaxWidth()
            .padding(horizontal = 12.dp, vertical = 4.dp),
        text = query,
        placeholder = "Search by name, city, or state",
        singleLine = true,
        trailingIcon = if (query.isNotEmpty()) {
            {
                IconButton(onClick = { onQueryChanged("") }) {
                    Icon(
                        imageVector = Icons.Default.Close,
                        contentDescription = "Clear search"
                    )
                }
            }
        } else null,
        onTextChange = onQueryChanged
    )
}

@OptIn(ExperimentalLayoutApi::class)
@Composable
private fun LocationsFilterChipRow(
    activeFilters: Set<LocationFilter>,
    onFilterToggled: (LocationFilter) -> Unit
) {
    FlowRow(
        modifier = Modifier
            .fillMaxWidth()
            .padding(horizontal = 12.dp, vertical = 4.dp),
        horizontalArrangement = Arrangement.spacedBy(6.dp),
        verticalArrangement = Arrangement.spacedBy((-8).dp)
    ) {
        Text(
            text = "Filter",
            fontSize = 14.sp,
            color = MaterialTheme.colorScheme.onSurface.copy(alpha = 0.6f),
            modifier = Modifier.align(Alignment.CenterVertically).padding(end = 4.dp)
        )
        LocationFilter.entries.forEach { filter ->
            val selected = filter in activeFilters
            FilterChip(
                selected = selected,
                onClick = { onFilterToggled(filter) },
                label = { Text(filter.name.lowercase().replaceFirstChar { it.uppercase() }) },
                border = FilterChipDefaults.filterChipBorder(
                    enabled = true,
                    borderColor = MaterialTheme.colorScheme.tertiary,
                    selected = selected
                ),
                colors = FilterChipDefaults.filterChipColors(
                    containerColor = MaterialTheme.colorScheme.background,
                    selectedContainerColor = MaterialTheme.colorScheme.tertiary,
                    selectedLabelColor = MaterialTheme.colorScheme.onTertiary
                )
            )
        }
    }
}

@OptIn(ExperimentalFoundationApi::class)
@Composable
private fun LocationRow(
    location: Location,
    selectionMode: Boolean,
    selected: Boolean,
    selectable: Boolean,
    onClick: () -> Unit,
    onLongClick: () -> Unit
) {
    val rowAlpha = if (selectionMode && !selectable) 0.4f else 1f
    Column(
        modifier = Modifier
            .fillMaxWidth()
            .then(
                if (selected) {
                    Modifier.background(MaterialTheme.colorScheme.tertiary.copy(alpha = 0.12f))
                } else {
                    Modifier
                }
            )
            .combinedClickable(
                enabled = !selectionMode || selectable,
                onClick = onClick,
                onLongClick = onLongClick
            )
    ) {
        Row(
            modifier = Modifier
                .fillMaxWidth()
                .padding(start = 16.dp, end = 16.dp, top = 12.dp, bottom = 12.dp),
            verticalAlignment = Alignment.CenterVertically
        ) {
            if (selectionMode) {
                Checkbox(
                    checked = selected,
                    onCheckedChange = null,
                    enabled = selectable,
                    colors = CheckboxDefaults.colors(
                        checkedColor = MaterialTheme.colorScheme.tertiary,
                        checkmarkColor = MaterialTheme.colorScheme.onTertiary
                    )
                )
                Spacer(modifier = Modifier.width(12.dp))
            }
            Column(modifier = Modifier.weight(1f)) {
                Text(
                    text = location.name.ifEmpty { "(no name)" },
                    fontSize = 16.sp,
                    fontWeight = FontWeight.SemiBold,
                    color = MaterialTheme.colorScheme.onSurface.copy(alpha = rowAlpha),
                    maxLines = 1,
                    overflow = TextOverflow.Ellipsis
                )
                Text(
                    text = "${location.city}, ${location.state}",
                    fontSize = 14.sp,
                    color = MaterialTheme.colorScheme.secondary.copy(alpha = rowAlpha),
                    maxLines = 1,
                    overflow = TextOverflow.Ellipsis
                )
            }
            Text(
                text = location.type.name.lowercase().replaceFirstChar { it.uppercase() },
                color = MaterialTheme.colorScheme.tertiary.copy(alpha = rowAlpha),
                fontSize = 14.sp,
                fontWeight = FontWeight.SemiBold,
                maxLines = 1
            )
            if (location.deleted) {
                Text(
                    text = " · Deleted",
                    color = MaterialTheme.colorScheme.error,
                    fontSize = 14.sp,
                    maxLines = 1
                )
            }
        }
        HorizontalDivider()
    }
}

@Composable
private fun MergeLocationsDialog(
    locations: List<Location>,
    isMerging: Boolean,
    onDismiss: () -> Unit,
    onConfirm: (keeperId: String) -> Unit
) {
    // Default the keeper to the first selected location.
    var keeperId by remember(locations) {
        mutableStateOf(locations.firstOrNull()?.id ?: "")
    }

    Dialog(onDismissRequest = { if (!isMerging) onDismiss() }) {
        Card(
            modifier = Modifier.responsiveDialogWidth(),
            shape = RoundedCornerShape(16.dp)
        ) {
            Column(
                modifier = Modifier
                    .fillMaxWidth()
                    .padding(16.dp)
            ) {
                Text(
                    text = "Merge Locations",
                    fontSize = 20.sp,
                    fontWeight = FontWeight.Bold
                )
                Spacer(modifier = Modifier.height(8.dp))
                Text(
                    text = "Choose the location to keep. All mileage entries from the " +
                        "others will be moved to it, and the other locations will be " +
                        "permanently deleted.",
                    fontSize = 14.sp,
                    color = MaterialTheme.colorScheme.secondary
                )
                Spacer(modifier = Modifier.height(12.dp))

                locations.forEach { location ->
                    Row(
                        modifier = Modifier
                            .fillMaxWidth()
                            .clickable(enabled = !isMerging) { keeperId = location.id }
                            .padding(vertical = 4.dp),
                        verticalAlignment = Alignment.CenterVertically
                    ) {
                        RadioButton(
                            selected = location.id == keeperId,
                            onClick = { keeperId = location.id },
                            enabled = !isMerging,
                            colors = RadioButtonDefaults.colors(
                                selectedColor = MaterialTheme.colorScheme.tertiary
                            )
                        )
                        Spacer(modifier = Modifier.width(8.dp))
                        Column(modifier = Modifier.weight(1f)) {
                            Text(
                                text = location.name.ifEmpty { "(no name)" },
                                fontSize = 16.sp,
                                fontWeight = FontWeight.SemiBold,
                                maxLines = 1,
                                overflow = TextOverflow.Ellipsis
                            )
                            Text(
                                text = "${location.city}, ${location.state}",
                                fontSize = 14.sp,
                                color = MaterialTheme.colorScheme.secondary,
                                maxLines = 1,
                                overflow = TextOverflow.Ellipsis
                            )
                        }
                    }
                }

                Spacer(modifier = Modifier.height(8.dp))

                if (isMerging) {
                    Box(modifier = Modifier.fillMaxWidth(), contentAlignment = Alignment.Center) {
                        CircularProgressIndicator(color = MaterialTheme.colorScheme.tertiary)
                    }
                } else {
                    DialogButtonRow(
                        confirmButtonText = "Merge",
                        confirmEnabled = keeperId.isNotEmpty() && locations.size >= 2,
                        onConfirmClick = { onConfirm(keeperId) },
                        dismissButtonText = "Cancel",
                        onDismissClick = onDismiss
                    )
                }
            }
        }
    }
}

